import { AccessLevel } from '@/interfaces/Access.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import { BuildingsProvider } from '@/providers/buildings/buildings.provider';
import { ProfileProvider } from '@/providers/profile/profile.provider';
import { NEW_BUILDINGS_PAGE } from '@/providers/rights.constants';
import { inject, Injectable } from '@angular/core';
import { type ActivatedRouteSnapshot, type CanActivateChildFn, type CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, type Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class BuildingVisualizationFeatureService {
    private _router = inject(Router);
    private _profileProvider = inject(ProfileProvider);

    goToFeature() {
        this._router.navigate(['/', 'buildings']);
    }

    goToBuildingDetails(buildingId: BuildingId) {
        this._router.navigate(['/', 'buildings', buildingId]);
    }

    goToBuildingCreation() {
        this._router.navigate(['/', 'buildings', 'building-creation']);
    }

    hasFeatureAccess$(): Observable<boolean> {
        return this._profileProvider.hasRight$(NEW_BUILDINGS_PAGE);
    }

    hasFeatureAccess(): Promise<boolean> {
        return this._profileProvider.hasRight(NEW_BUILDINGS_PAGE);
    }
}

export const buildingVisualizationGuard: CanActivateFn & CanActivateChildFn = async () => {
    const featureService = inject(BuildingVisualizationFeatureService);
    const hasFeatureAccess = await featureService.hasFeatureAccess();

    if (!hasFeatureAccess) {
        // redirect ?
        return false;
    }
    return true;
};

export const buildingDetailsGuard: CanActivateFn & CanActivateChildFn = async (route: ActivatedRouteSnapshot) => {
    const buildingsProvider = inject(BuildingsProvider);
    const buildingId = route.params.buildingId;

    const hasBuildingAccess = await firstValueFrom(buildingsProvider.hasStableMinimumBuildingAccess$(buildingId, AccessLevel.READ));

    if (!hasBuildingAccess) {
        // redirect ?
        return false;
    }
    return true;
};
